import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, Shield, Pencil } from 'lucide-react';
import { liteApi } from '@/utils/lite/liteApi';

/**
 * DeIdPanel — shared "Step 2" UI.
 *
 * Props:
 *  - text: string (the user's input to be scanned)
 *  - onProceed(cleanedText): called when user accepts redactions
 *  - onCancel(): go back to inputs
 *  - autoRunOnEmpty: if true and text is empty, will skip & call onProceed('')
 */
export default function DeIdPanel({ text, onProceed, onCancel }) {
  const [scanning, setScanning] = useState(false);
  const [flags, setFlags] = useState(null);
  const [cleaned, setCleaned] = useState(text || '');
  const [hasBlocking, setHasBlocking] = useState(false);
  const [editText, setEditText] = useState(text || '');
  const [manualEdit, setManualEdit] = useState(false);
  const [error, setError] = useState('');

  const runScan = async (txt) => {
    setScanning(true);
    setError('');
    try {
      const res = await liteApi.deidentify(txt);
      setFlags(res.flags || []);
      setCleaned(res.cleaned_text || txt);
      setHasBlocking(res.has_blocking);
    } catch (e) {
      setError(e.message || 'Scan failed.');
    } finally {
      setScanning(false);
    }
  };

  // Run scan on first mount
  useMemo(() => { runScan(text || ''); /* eslint-disable-next-line */ }, []);

  const flagCounts = useMemo(() => {
    const counts = {};
    (flags || []).forEach((f) => { counts[f.type] = (counts[f.type] || 0) + 1; });
    return counts;
  }, [flags]);

  const noFlags = flags && flags.length === 0;

  return (
    <div className="space-y-4" data-testid="deid-panel">
      <div className="flex items-start gap-3 p-4 rounded-xl bg-stone-50 border border-stone-200">
        <Shield className="w-5 h-5 text-emerald-600 mt-0.5" />
        <div className="text-sm text-stone-700">
          <p className="font-medium text-stone-900 mb-1">Clean your input</p>
          <p>
            We scan for names, dates, addresses, schools, phone numbers and emails before
            anything is sent to AI processing. Nothing is stored or linked to you.
          </p>
        </div>
      </div>

      {scanning && (
        <div className="text-sm text-stone-500" data-testid="deid-scanning">
          Scanning for identifiers…
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600" data-testid="deid-error">{error}</div>
      )}

      {!scanning && noFlags && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="deid-clean">
          <div className="flex items-center gap-2 text-emerald-900 font-medium mb-1">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <span>Scan complete — no identifiers detected</span>
          </div>
          <p className="text-sm text-emerald-800 leading-snug">
            We checked your input for names, dates, addresses, schools, phone numbers,
            emails, SSN and MRN patterns. None were found. You can review your input
            below before proceeding — if you used any indirect identifiers (e.g. "my
            youngest patient", a unique diagnosis combination), please remove them
            manually.
          </p>
          <div className="mt-3 p-3 rounded-lg bg-white/60 border border-emerald-100 text-sm text-stone-700 max-h-[160px] overflow-y-auto whitespace-pre-wrap">
            {text || <em className="text-stone-400">(empty input)</em>}
          </div>
        </div>
      )}

      {!scanning && flags && flags.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-stone-500">Original</p>
            {manualEdit ? (
              <textarea
                data-testid="deid-edit-textarea"
                className="w-full min-h-[200px] p-3 rounded-xl border border-stone-200 text-sm font-mono"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
              />
            ) : (
              <div className="p-3 rounded-xl border border-stone-200 bg-white text-sm whitespace-pre-wrap">
                {text}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              data-testid="deid-toggle-edit"
              onClick={() => {
                if (manualEdit) {
                  // Re-scan after manual edit
                  runScan(editText);
                }
                setManualEdit(!manualEdit);
              }}
              className="text-stone-600"
            >
              <Pencil className="w-3.5 h-3.5 mr-1" />
              {manualEdit ? 'Re-scan' : 'Edit manually'}
            </Button>
          </div>

          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-stone-500">
              {flags.length} flag{flags.length === 1 ? '' : 's'} found
            </p>
            <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/50 max-h-[300px] overflow-y-auto space-y-2">
              {flags.map((f, i) => (
                <div
                  key={i}
                  data-testid={`deid-flag-${i}`}
                  className="flex items-start gap-2 text-sm"
                >
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="font-medium text-stone-900">{f.type}</span>
                    <span className="text-stone-500"> · </span>
                    <span className="text-stone-700">"{f.matched}"</span>
                    <span className="text-stone-500"> → </span>
                    <span className="font-mono text-emerald-700">{f.suggestion}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-stone-500">
              {Object.entries(flagCounts).map(([k, v]) => `${v} ${k}`).join(' · ')}
            </p>
          </div>
        </div>
      )}

      {hasBlocking && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="deid-blocking-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <span>High-confidence identifiers detected (e.g. email, phone, SSN). Click "Clean &amp; proceed" to redact them before generating.</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <Button
          variant="outline"
          data-testid="deid-back-btn"
          onClick={onCancel}
          className="rounded-full"
        >
          Back
        </Button>
        <Button
          data-testid="deid-proceed-btn"
          onClick={() => onProceed(cleaned)}
          disabled={scanning}
          className="rounded-full"
        >
          {flags && flags.length > 0 ? 'Clean & proceed' : 'Proceed'}
        </Button>
      </div>
    </div>
  );
}
